/**
 * Redaction helpers for secrets that appear in Studio URLs.
 *
 * Several Studio routes carry a single-use secret in the URL — password reset
 * tokens, email verification tokens, org invitation join tokens, email
 * preference tokens and OAuth authorization codes. Browsers and third-party
 * telemetry SDKs copy the full URL into places we do not control:
 *
 *   - the `Referer` request header (mitigated separately via `Referrer-Policy`
 *     headers in `netlify.toml`)
 *   - analytics payloads, which embed `window.location.href` and
 *     `document.referrer` on every event (Datadog RUM `view.url`/`view.referrer`,
 *     GA4 `dl`/`dr`, Amplitude event properties)
 *
 * Those payloads are sent to `api2.amplitude.com`,
 * `browser-intake-datadoghq.com` and `region1.analytics.google.com`, which puts
 * live account-takeover tokens into third-party access logs.
 *
 * Everything here must be total: analytics code runs on every page and must
 * never throw. On any unexpected input we fall back to redacting more, never
 * less. Exception: the payload walkers throw past `MAX_PAYLOAD_DEPTH`.
 *
 * See OE-1393 / Doyensec APS-Q226-11 (previously OE-872 / APO-Q125-06).
 */

export const REDACTED = 'REDACTED';

/**
 * Query parameter names whose values are secrets. Matched case-insensitively.
 *
 * Keep in sync with the token params accepted by route configs — see
 * `src/app/security-headers.test.ts`, which asserts the corresponding
 * `Referrer-Policy` coverage.
 */
const SENSITIVE_QUERY_PARAMS: readonly string[] = [
  // password reset (`/reset-password`), email verification (`/verify-email`),
  // email preferences (`/email-preferences`)
  'token',
  // org invitations carried through the onboarding funnel
  // (`/signup`, `/welcome`, `/survey`, `/login`)
  'joinToken',
  // embedded Explorer auth (`/embed-authentication`)
  'inviteToken',
  'resetToken',
  // OAuth / OIDC secrets, most notably on `/explorer-oauth2`, whose route
  // config allows unknown keys through
  'code',
  'access_token',
  'accessToken',
  'id_token',
  'idToken',
  'refresh_token',
  'refreshToken',
  'apiKey',
  'api_key',
];

const SENSITIVE_QUERY_PARAM_SET = new Set(
  SENSITIVE_QUERY_PARAMS.map((param) => param.toLowerCase()),
);

/**
 * Path patterns whose capture group is a secret path segment rather than a
 * query param. `/org/:orgId/invite/:joinToken` is the only one today.
 */
const SENSITIVE_PATH_SEGMENT_PATTERNS: readonly RegExp[] = [
  /(\/invite\/)[^/?#]+/gi,
];

export function isSensitiveQueryParam(name: string): boolean {
  return SENSITIVE_QUERY_PARAM_SET.has(name.toLowerCase());
}

/**
 * The decoded name of a `name=value` pair, or `null` when the pair carries no
 * value and so cannot be leaking one.
 */
function paramName(pair: string): string | null {
  const separatorIndex = pair.indexOf('=');
  if (separatorIndex === -1) return null;
  const rawName = pair.slice(0, separatorIndex);
  try {
    return decodeURIComponent(rawName.replace(/\+/g, ' '));
  } catch {
    // Malformed percent-encoding — fall back to the raw name.
    return rawName;
  }
}

/**
 * Redacts secret path segments (e.g. the join token in
 * `/org/apollo/invite/abc123`) while leaving the rest of the path intact so
 * analytics can still attribute the pageview to a route.
 */
export function redactSensitivePathname(pathname: string): string {
  if (typeof pathname !== 'string' || !pathname) return pathname;
  return SENSITIVE_PATH_SEGMENT_PATTERNS.reduce(
    (acc, pattern) => acc.replace(pattern, `$1${REDACTED}`),
    pathname,
  );
}

/**
 * Redacts the values of sensitive params in a query string. Accepts the string
 * with or without a leading `?` and preserves that form in the output.
 *
 * Parsed and re-serialized manually rather than with `URLSearchParams` so that
 * the encoding of untouched params is preserved byte for byte — analytics
 * dedupe on exact URL strings.
 */
export function redactSensitiveSearch(search: string): string {
  if (typeof search !== 'string' || !search) return search;

  const hasLeadingQuestionMark = search.startsWith('?');
  const body = hasLeadingQuestionMark ? search.slice(1) : search;
  if (!body) return search;

  const redacted = body
    .split('&')
    .map((pair) => {
      if (!pair) return pair;
      const name = paramName(pair);
      if (name === null || !isSensitiveQueryParam(name)) return pair;
      return `${pair.slice(0, pair.indexOf('='))}=${REDACTED}`;
    })
    .join('&');

  return hasLeadingQuestionMark ? `?${redacted}` : redacted;
}

/**
 * Whether a URL fragment carries a secret. Fragments are all-or-nothing: we
 * never need their contents for analytics, so any hint of a secret redacts the
 * whole thing.
 *
 * Matching on the substring `token` alone missed the OAuth authorization code,
 * which `/explorer-oauth2` receives in the fragment whenever the provider uses
 * `response_mode=fragment`, so the parameter names are checked too.
 */
function hashCarriesSecret(hash: string): boolean {
  // Broad net first: any mention of a token goes, whatever shape it is in.
  if (/token/i.test(hash)) return true;

  const body = hash.startsWith('#') ? hash.slice(1) : hash;
  if (!body) return false;

  // Split on both separators: an OAuth fragment is `a=1&b=2`, while a
  // hash-routed URL nests a query string inside the fragment (`#/route?code=1`).
  return body.split(/[?&]/).some((pair) => {
    const name = paramName(pair);
    return name !== null && isSensitiveQueryParam(name);
  });
}

/**
 * Redacts secrets from a URL's path, query string and fragment, leaving origin
 * and all non-sensitive params intact.
 *
 * Handles absolute URLs, protocol-relative URLs and path-only strings. Returns
 * non-string / empty input unchanged so it can be applied defensively to
 * optional telemetry fields.
 *
 * A string that holds no secret is returned exactly as it came in. Analytics
 * properties sitting under URL-like keys are frequently not URLs at all — a
 * `location` of `sidebar`, a `path` of `Unnamed`, a bare `referrer` host —
 * and round-tripping those through `URL` would rewrite them (`sidebar` becomes
 * `/sidebar`), silently corrupting dimensions across every SDK.
 */
export function redactSensitiveUrl<T extends string | undefined | null>(
  url: T,
): T {
  if (typeof url !== 'string' || !url) return url;
  return (redactSensitiveUrlOrNull(url) ?? url) as T;
}

/**
 * The body of `redactSensitiveUrl`, returning `null` when there was nothing to
 * redact so the caller can hand back the original string untouched.
 */
function redactSensitiveUrlOrNull(url: string): string | null {
  // Bare query strings and fragments (e.g. `location.search`)
  // must not be run through `URL`, which would prepend a `/` path.
  if (url.startsWith('?')) {
    const redactedSearch = redactSensitiveSearch(url);
    return redactedSearch === url ? null : redactedSearch;
  }
  if (url.startsWith('#')) {
    return hashCarriesSecret(url) ? `#${REDACTED}` : null;
  }

  try {
    // `URL` needs a base for relative inputs; the base is stripped back off
    // below so path-only inputs stay path-only.
    const base = 'http://redacted.invalid';
    const parsed = new URL(url, base);

    const pathname = redactSensitivePathname(parsed.pathname);
    const search = redactSensitiveSearch(parsed.search);
    const redactHash = !!parsed.hash && hashCarriesSecret(parsed.hash);

    // Nothing sensitive: return the input rather than `URL`'s normalization of
    // it, which rewrites spaces, non-ASCII and protocol-relative origins.
    if (
      pathname === parsed.pathname &&
      search === parsed.search &&
      !redactHash
    ) {
      return null;
    }

    parsed.pathname = pathname;
    parsed.search = search;
    if (redactHash) parsed.hash = `#${REDACTED}`;

    const serialized = parsed.toString();
    return serialized.startsWith(base) && !url.startsWith(base)
      ? serialized.slice(base.length)
      : serialized;
  } catch {
    // Unparseable URL: fall back to a string-level pass so we still strip the
    // obvious cases rather than leaking the whole thing.
    const [pathAndSearch = '', ...hashParts] = url.split('#');
    const [path = '', ...searchParts] = pathAndSearch.split('?');
    const search = searchParts.join('?');
    const hash = hashParts.length ? `#${hashParts.join('#')}` : '';

    const redactedPath = redactSensitivePathname(path);
    const redactedSearch = redactSensitiveSearch(search);
    const redactHash = !!hash && hashCarriesSecret(hash);

    if (redactedPath === path && redactedSearch === search && !redactHash) {
      return null;
    }

    return `${redactedPath}${redactedSearch ? `?${redactedSearch}` : ''}${
      redactHash ? `#${REDACTED}` : hash
    }`;
  }
}

/** The current page URL, safe to hand to a third-party analytics SDK. */
export function getRedactedHref(): string {
  return redactSensitiveUrl(window.location.href);
}

/** The current `document.referrer`, safe to hand to a third-party SDK. */
export function getRedactedReferrer(): string {
  return redactSensitiveUrl(document.referrer);
}

/**
 * Keys whose string values are URLs, matched on the last word so vendor
 * prefixes come along for free.
 *
 * The separator class includes whitespace because Amplitude names its
 * properties `[Amplitude] Page URL`, `[Amplitude] Page Location` and
 * `[Amplitude] Page Path` — space-separated, and the place the page URL
 * actually lands on an Amplitude event.
 */
const URL_LIKE_KEY =
  /(^|[_.\s])(url|href|referrer|referer|location|path|pathname|search|query_string|page_location|page_referrer|dl|dr|form_destination)$/i;

/** Past this depth (or on a cycle) the walkers throw, so callers drop the event. */
const MAX_PAYLOAD_DEPTH = 32;

/**
 * Whether a value is a plain data object, i.e. safe to rebuild key by key.
 *
 * Anything else that is `typeof 'object'` — a `Date`, a `RegExp`, a `Map`,
 * any class instance — loses its prototype and its internal state when copied
 * that way, so those are passed through untouched.
 */
function isPlainObject(value: unknown): value is Record<string, unknown> {
  if (value === null || typeof value !== 'object') return false;
  const prototype = Object.getPrototypeOf(value) as unknown;
  return prototype === Object.prototype || prototype === null;
}

function redactContainer(value: unknown, depth: number): unknown {
  if (value == null || typeof value !== 'object') return value;
  if (depth > MAX_PAYLOAD_DEPTH) {
    throw new Error('Analytics payload too deep to redact');
  }

  const walk = (child: unknown) => redactContainer(child, depth + 1);

  if (Array.isArray(value)) return value.map(walk);

  if (!isPlainObject(value)) return value;

  const target: Record<string, unknown> = {};
  for (const [key, child] of Object.entries(value)) {
    target[key] =
      typeof child === 'string' && URL_LIKE_KEY.test(key)
        ? redactSensitiveUrl(child)
        : walk(child);
  }
  return target;
}

/**
 * Deep-redacts URL-like string values in an arbitrary analytics payload and
 * returns a redacted copy, leaving the input untouched.
 *
 * Used for SDKs (Amplitude) where the full URL shows up in vendor-defined
 * properties we do not enumerate ourselves, and whose shape can change between
 * SDK versions.
 */
export function redactUrlsInPayload<T>(payload: T): T {
  return redactContainer(payload, 0) as T;
}
